import { useEffect, useState } from 'react';
import { X, Crop, Image as ImageIcon, FileImage, Loader2, ShieldCheck, Eye, Lock, Share2 } from 'lucide-react';
import { Switch } from './ui/switch';

/**
 * Export Modal — Free preview vs paid HD.
 * Props:
 *   open, onClose
 *   onFreePreview(format)
 *   onBuyHd(format, crop, shareLink)
 *   busyMode: null | 'free' | 'paid'
 *   paymentStatus: null | 'awaiting' | 'paid' | 'cancelled' | 'expired'
 */
export const ExportModal = ({
  open,
  onClose,
  onFreePreview,
  onBuyHd,
  busyMode,
  paymentStatus,
}) => {
  const [format, setFormat] = useState('png');
  const [crop, setCrop] = useState(false);
  const [shareLink, setShareLink] = useState(true);

  // Reset transient picks when modal closes
  useEffect(() => {
    if (!open) {
      setFormat('png');
      setCrop(false);
      setShareLink(true);
    }
  }, [open]);

  if (!open) return null;

  const isAwaitingPayment = paymentStatus === 'awaiting';

  return (
    <div
      data-testid="export-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40"
      onClick={() => !busyMode && onClose()}
    >
      <div
        data-testid="export-modal"
        className="relative w-full max-w-lg bg-paper border-2 border-ink shadow-brutXl rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-start justify-between p-5 border-b-2 border-ink">
          <div>
            <p className="label-tiny mb-1">Export your collage</p>
            <h2 className="font-outfit font-black text-2xl leading-tight">
              Pick how you want to download
            </h2>
          </div>
          <button
            data-testid="export-modal-close"
            onClick={onClose}
            disabled={!!busyMode}
            aria-label="Close"
            className="neo-btn bg-paper h-9 w-9 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* options */}
        <div className="p-5 space-y-4">
          {/* Format selector */}
          <div>
            <p className="label-tiny mb-2">Format</p>
            <div className="grid grid-cols-2 gap-2">
              <FormatChip
                testid="format-png"
                active={format === 'png'}
                onClick={() => setFormat('png')}
                icon={<ImageIcon className="h-4 w-4" />}
                label="PNG"
                sub="transparency"
              />
              <FormatChip
                testid="format-jpg"
                active={format === 'jpg'}
                onClick={() => setFormat('jpg')}
                icon={<FileImage className="h-4 w-4" />}
                label="JPG"
                sub="flattened"
              />
            </div>
          </div>

          {/* Crop toggle */}
          <div
            data-testid="modal-crop-row"
            className="flex items-start justify-between gap-3 p-3 border-2 border-ink rounded-sm bg-canvasbg"
          >
            <div className="flex items-start gap-2">
              <Crop className="h-4 w-4 mt-0.5" />
              <div>
                <p className="font-outfit font-bold text-sm">Crop to content</p>
                <p className="font-work text-xs text-ink/60 leading-tight">
                  Auto-trim empty space — applies to HD download.
                </p>
              </div>
            </div>
            <Switch
              data-testid="modal-crop-switch"
              checked={crop}
              onCheckedChange={setCrop}
              className="border-2 border-ink data-[state=checked]:bg-ink"
            />
          </div>

          {/* Share-link toggle (HD only) */}
          <div
            data-testid="modal-share-row"
            className="flex items-start justify-between gap-3 p-3 border-2 border-ink rounded-sm bg-canvasbg"
          >
            <div className="flex items-start gap-2">
              <Share2 className="h-4 w-4 mt-0.5" />
              <div>
                <p className="font-outfit font-bold text-sm">Generate share link</p>
                <p className="font-work text-xs text-ink/60 leading-tight">
                  Mint a 30-day public gift page. Off = private download only, no public URL.
                </p>
              </div>
            </div>
            <Switch
              data-testid="modal-share-switch"
              checked={shareLink}
              onCheckedChange={setShareLink}
              className="border-2 border-ink data-[state=checked]:bg-ink"
            />
          </div>

          {/* Free preview */}
          <button
            data-testid="export-free-btn"
            disabled={!!busyMode}
            onClick={() => onFreePreview(format)}
            className="neo-btn bg-paper w-full p-4 flex items-start justify-between disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-left">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span className="font-outfit font-bold text-base">Free preview</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink/60 border border-ink/40 px-1 py-0.5">
                  512 px · watermarked
                </span>
              </div>
              <p className="font-work text-xs text-ink/60 mt-1 leading-tight">
                Low-res with a STCKRSTCKR watermark. Great for testing.
              </p>
            </div>
            <div className="h-9 w-9 border-2 border-ink bg-mint shadow-brutSm flex items-center justify-center">
              {busyMode === 'free' ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="font-outfit font-black text-lg">$0</span>}
            </div>
          </button>

          {/* Paid HD */}
          <button
            data-testid="export-buy-btn"
            disabled={!!busyMode}
            onClick={() => onBuyHd(format, crop, shareLink)}
            className="neo-btn bg-lilac w-full p-4 flex items-start justify-between disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-left">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span className="font-outfit font-bold text-base">HD download</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink/70 border border-ink/40 px-1 py-0.5">
                  5000 px · clean
                </span>
              </div>
              <p className="font-work text-xs text-ink/70 mt-1 leading-tight">
                Full-res, no watermark. Crop applied if enabled above.
              </p>
            </div>
            <div className="h-10 px-3 border-2 border-ink bg-paper shadow-brutSm flex items-center justify-center font-outfit font-black text-base">
              {busyMode === 'paid' ? <Loader2 className="h-4 w-4 animate-spin" /> : '$1.99'}
            </div>
          </button>

          {/* Payment status */}
          {isAwaitingPayment && (
            <div data-testid="payment-status" className="border-2 border-ink bg-sun/40 p-3 rounded-sm">
              <p className="font-work text-xs leading-relaxed">
                <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                <span className="font-outfit font-bold">Waiting for payment…</span> A Stripe Checkout window has opened. Complete the payment there — your HD file will start downloading automatically.
              </p>
            </div>
          )}
          {paymentStatus === 'cancelled' && (
            <div data-testid="payment-cancelled" className="border-2 border-ink bg-rose/40 p-3 rounded-sm">
              <p className="font-work text-xs">Payment cancelled. You can try again or grab a free preview.</p>
            </div>
          )}

          {/* Trust strip */}
          <div className="flex items-center gap-2 text-xs text-ink/60">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="font-work">Secured by Stripe · Test mode</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FormatChip = ({ active, onClick, icon, label, sub, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center justify-between p-3 border-2 border-ink rounded-sm transition-all ${active ? 'bg-lilac shadow-brut -translate-y-0.5' : 'bg-paper shadow-brutSm hover:-translate-y-0.5'}`}
  >
    <div className="flex items-center gap-2">
      {icon}
      <span className="font-outfit font-bold text-sm">{label}</span>
    </div>
    <span className="font-mono text-[10px] uppercase tracking-wider text-ink/60">{sub}</span>
  </button>
);
